// --- Dependencies -------------------------------------------------------------------------------
import { useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyFile } from "@/types";
import { pressToOpen, rightClickToOpen } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = { file: TyFile; onOpen: () => void; onActions: (anchor: DOMRect) => void };

/**
 * One picture in a gallery strip. `FileCard` with the text taken off: in a gallery the picture *is*
 * the identity, and a name under every tile turns a row of photographs into a row of rows.
 *
 * The thumbnail fades in once it has decoded, over the icon underneath — hidden with `opacity` and
 * never `display: none`, for the reason spelled out in `FileCard`.
 */
export default function PhotoCard({ file, onOpen, onActions }: Props) {
    const t = useTranslation();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [thumb, setThumb] = useState<"loading" | "ready" | "failed">(file.thumb.url ? "loading" : "failed");

    return (
        <article
            className="photo-card"
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={pressToOpen(onOpen)}
            onContextMenu={rightClickToOpen(onActions)}
            title={file.name}
        >
            {file.thumb.url && (
                <img
                    className={`photo-card__thumb${thumb === "ready" ? " photo-card__thumb--ready" : ""}`}
                    src={file.thumb.url}
                    alt={file.name}
                    loading="lazy"
                    onLoad={() => setThumb("ready")}
                    onError={() => setThumb("failed")}
                />
            )}
            {thumb !== "ready" && <Icon name="image" size={26} />}

            {/* The one place on the tile that is **not** the tile: it opens a menu instead. */}
            <button
                ref={buttonRef}
                type="button"
                className="photo-card__more"
                onClick={(event) => {
                    event.stopPropagation();
                    const rect = buttonRef.current?.getBoundingClientRect();
                    if (rect) onActions(rect);
                }}
                aria-label={t.common.more}
            >
                <Icon name="more" size={16} />
            </button>
        </article>
    );
}
